import React, { FunctionComponent, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { SubmitButton, SlideIn } from 'components';
import { FormGroup } from 'components/chroma';

import { getApiData } from 'scenes/common/helpers/apiData';
import { InputFormatQuestion } from 'scenes/common/components/Questions';

import {
  VehicleModification,
  MotorFormState,
  MotorFullPageProps,
} from 'scenes/motor/types';
import { getStereoCost, getVehicleModifications } from 'scenes/motor/selectors';

import ModificationList from './ModificationList';
import WhyItIsImportantModal from './WhyItIsImportantModal';

const STEREO_COST = 'stereoCost';
const STEREO_OPTION = 'MODSTEREO';
const STEREO_COST_MAX_LENGTH = 11;

const stereoSelected = (modifications: VehicleModification[]): boolean =>
  modifications.some((modification) => modification.name === STEREO_OPTION);

const ModificationsSelectionStep: FunctionComponent<MotorFullPageProps> = ({
  funnelState,
  onNext,
}) => {
  const { formState: hooksForm, handleSubmit, errors, control } = useForm({
    mode: 'onChange',
  });
  const [showModal, setShowModal] = useState(false);
  const [
    selectedVehicleModifications,
    setSelectedVehicleModifications,
  ] = useState(getVehicleModifications(funnelState));

  const { vehicleModifications } = getApiData(
    funnelState.apiResponses.quoteDetails
  );

  const onSubmit = useCallback(
    (values: MotorFormState) => {
      onNext({
        ...values,
        vehicleModifications: selectedVehicleModifications,
      });
    },
    [onNext, selectedVehicleModifications]
  );

  const handleSelect = useCallback(
    (vehicleModification: VehicleModification): void => {
      const copySelectedModifications = [...selectedVehicleModifications];
      const foundIndex = selectedVehicleModifications?.findIndex(
        (selectedModification) =>
          selectedModification.name === vehicleModification.name
      );

      if (foundIndex > -1) {
        copySelectedModifications.splice(foundIndex, 1);
      } else {
        copySelectedModifications.push(vehicleModification);
      }

      setSelectedVehicleModifications(copySelectedModifications);
    },
    [selectedVehicleModifications]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup label="Which of the following does the car have?">
        <ModificationList
          vehicleModifications={vehicleModifications}
          selectedModifications={selectedVehicleModifications}
          onClick={handleSelect}
        />
      </FormGroup>

      {stereoSelected(selectedVehicleModifications) && (
        <SlideIn>
          <InputFormatQuestion
            size={4}
            prefix="$"
            ariaRequired
            decimalScale={0}
            isNumericString
            errors={errors}
            placeholder="$0"
            control={control}
            stateKey={STEREO_COST}
            question="How much did the after market car sound system cost?"
            helpLink={{
              id: 'modal-link',
              text: "Why it's important?",
              ariaLabel: "Why it's important? Opens dialog",
              onClick: () => setShowModal(true),
            }}
            maxLength={STEREO_COST_MAX_LENGTH}
            defaultValue={getStereoCost(funnelState)}
            validationRules={{
              required: true,
              validate: {
                validCost: (value) => value > 0,
              },
            }}
          />
        </SlideIn>
      )}

      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
      <WhyItIsImportantModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </form>
  );
};

export default ModificationsSelectionStep;
